import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Mail, Lock, User, Phone, Eye, EyeOff, ArrowLeft, Tag, CheckCircle, LogIn, UserPlus, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthPage = ({ onLogin }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgot, setShowForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '+971 ',
    whatsapp: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });

        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        onLogin(response.data.user);
        navigate('/');
      } else {
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone: formData.phone || null,
          whatsapp: formData.whatsapp || null
        });

        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        onLogin(response.data.user);
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || (i18n.language === 'ar' ? 'حدث خطأ. حاول مرة أخرى.' : 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setResetSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || (i18n.language === 'ar' ? 'حدث خطأ. حاول مرة أخرى.' : 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 focus:bg-white/8 text-white placeholder-white/25 text-sm transition-all";
  const iconClass = "absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30";

  // Forgot Password View
  if (showForgot) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] pb-40 md:pb-8">
        {/* Header */}
        <div className="hero-gradient relative">
          <div className="noise-overlay" />
          <div className="max-w-md mx-auto px-4 pt-4 pb-6 relative z-10">
            <button
              onClick={() => { setShowForgot(false); setResetSent(false); setError(''); }}
              className="w-10 h-10 rounded-xl bg-white/8 backdrop-blur-sm flex items-center justify-center hover:bg-white/15 transition-all active:scale-90 mb-5"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="animate-fade-up">
              <h1 className="text-2xl font-extrabold text-white font-heading tracking-tight">
                {i18n.language === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
              </h1>
              <p className="text-white/40 text-sm mt-1">
                {i18n.language === 'ar'
                  ? 'أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة تعيين'
                  : "Enter your email and we'll send you a reset link"}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-md mx-auto px-4 pt-5">
          {resetSent ? (
            <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-8 text-center animate-fade-up">
              <div className="w-16 h-16 bg-green-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-lg font-bold text-white mb-2 font-heading">
                {i18n.language === 'ar' ? 'تم إرسال الرابط!' : 'Reset Link Sent!'}
              </h2>
              <p className="text-white/40 text-sm mb-6 leading-relaxed">
                {i18n.language === 'ar'
                  ? 'تحقق من بريدك الإلكتروني وانقر على الرابط لإعادة تعيين كلمة المرور. تحقق أيضاً من مجلد البريد العشوائي. الرابط صالح لمدة 15 دقيقة.'
                  : 'Check your email and click the link to reset your password. Also check your spam/junk folder. The link expires in 15 minutes.'}
              </p>
              <button
                onClick={() => { setShowForgot(false); setResetSent(false); }}
                className="w-full py-3.5 bg-[#E31C25] text-white rounded-xl font-bold text-sm hover:bg-[#C41820] transition-all shadow-lg shadow-[#E31C25]/20 active:scale-[0.98]"
                data-testid="back-to-login-after-reset"
              >
                {i18n.language === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login'}
              </button>
            </div>
          ) : (
            <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-6 animate-fade-up">
              {error && (
                <div className="mb-5 p-3.5 bg-[#E31C25]/10 border border-[#E31C25]/20 rounded-xl text-[#E31C25] text-sm font-medium" data-testid="auth-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail className={iconClass} />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className={inputClass}
                      placeholder={t('auth.emailPlaceholder')}
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-[#E31C25] text-white rounded-xl font-bold text-sm hover:bg-[#C41820] disabled:bg-white/10 disabled:text-white/30 transition-all shadow-lg shadow-[#E31C25]/20 active:scale-[0.98]"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{i18n.language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Send className="w-4 h-4" />
                      <span>{i18n.language === 'ar' ? 'إرسال رابط إعادة التعيين' : 'Send Reset Link'}</span>
                    </div>
                  )}
                </button>
              </form>

              <div className="mt-5 p-4 bg-white/[0.03] rounded-xl border border-white/[0.06] text-center">
                <p className="text-sm text-white/30 mb-1">
                  {i18n.language === 'ar' ? 'لا تستطيع الوصول لبريدك الإلكتروني؟' : "Can't access your email?"}
                </p>
                <a
                  href="mailto:findadz@hotmail.com"
                  className="text-sm text-[#E31C25] font-semibold hover:underline"
                  data-testid="contact-support-link"
                >
                  {i18n.language === 'ar' ? 'تواصل مع الدعم: findadz@hotmail.com' : 'Contact support: findadz@hotmail.com'}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main Login / Register View
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] pb-40 md:pb-8">
      {/* Header */}
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-md mx-auto px-4 pt-4 pb-6 relative z-10">
          <Link
            to="/"
            className="w-10 h-10 rounded-xl bg-white/8 backdrop-blur-sm flex items-center justify-center hover:bg-white/15 transition-all active:scale-90 mb-5 inline-flex"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </Link>
          <div className="animate-fade-up">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-[#E31C25] rounded-2xl flex items-center justify-center shadow-lg shadow-[#E31C25]/25">
                <Tag className="w-6 h-6 text-white" />
              </div>
              <span className="text-lg font-extrabold text-white font-heading tracking-tight">Findadz</span>
            </div>
            <h1 className="text-2xl font-extrabold text-white font-heading tracking-tight">
              {isLogin ? t('auth.welcomeBack') : t('auth.createAccount')}
            </h1>
            <p className="text-white/40 text-sm mt-1">
              {isLogin ? t('auth.loginSubtitle') : t('auth.registerSubtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-5">
        {/* Auth Card */}
        <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-6 animate-fade-up">
          {/* Error Message */}
          {error && (
            <div className="mb-5 p-3.5 bg-[#E31C25]/10 border border-[#E31C25]/20 rounded-xl text-[#E31C25] text-sm font-medium" data-testid="auth-error">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className={iconClass} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder={t('auth.emailPlaceholder')}
                  data-testid="auth-email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className={iconClass} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-12 py-3.5 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 focus:bg-white/8 text-white placeholder-white/25 text-sm transition-all"
                  placeholder="••••••••"
                  data-testid="auth-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link - Login only */}
            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setError(''); }}
                  className="text-sm text-[#E31C25] font-semibold hover:text-[#ff3a42] transition-colors"
                  data-testid="forgot-password-link"
                >
                  {i18n.language === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot password?'}
                </button>
              </div>
            )}

            {/* Register Only Fields */}
            {!isLogin && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                    {t('auth.fullName')}
                  </label>
                  <div className="relative">
                    <User className={iconClass} />
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      placeholder={t('auth.fullNamePlaceholder')}
                      data-testid="auth-fullname"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                    {t('auth.phoneRequired')} <span className="text-[#E31C25]">*</span>
                  </label>
                  <div className="relative">
                    <Phone className={iconClass} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      dir="ltr"
                      className={`${inputClass} text-left`}
                      placeholder={t('postAd.phonePlaceholder')}
                      data-testid="auth-phone"
                    />
                  </div>
                  <p className="text-xs text-white/25 mt-1.5">{t('auth.phoneChangeNote')}</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                    {t('auth.whatsapp')}
                  </label>
                  <div className="relative">
                    <Phone className={iconClass} />
                    <input
                      type="tel"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      dir="ltr"
                      className={`${inputClass} text-left`}
                      placeholder={t('auth.whatsappPlaceholder')}
                      data-testid="auth-whatsapp"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-[#E31C25] text-white rounded-xl font-bold text-sm hover:bg-[#C41820] disabled:bg-white/10 disabled:text-white/30 transition-all shadow-lg shadow-[#E31C25]/20 active:scale-[0.98]"
              data-testid="auth-submit"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t('auth.loading')}</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  {isLogin ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                  <span>{isLogin ? t('auth.loginButton') : t('auth.registerButton')}</span>
                </div>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/[0.06]" />
            <span className="text-xs text-white/20 font-medium">{i18n.language === 'ar' ? 'أو' : 'OR'}</span>
            <div className="flex-1 h-px bg-white/[0.06]" />
          </div>

          {/* Toggle Auth Mode */}
          <div className="text-center">
            <p className="text-white/40 text-sm">
              {isLogin ? t('auth.noAccount') : t('auth.haveAccount')}{' '}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="text-[#E31C25] font-bold hover:text-[#ff3a42] transition-colors"
                data-testid="auth-toggle"
              >
                {isLogin ? t('auth.register') : t('auth.login')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
